import React, { useEffect, useState, useMemo } from "react";

// UX
import CardWorkPlanUX from "./CardWorkPlanUX";
import CardProjectList from "./CardProjectList";
import CardIssueList from "./CardIssueList";

// Common
import SnackMessage from "react-lib/apps/common/SnackMessage";

import { ADMIN_TAB_LIST } from "react-lib/apps/USER/CardSettingDivision";

// Interface
import {
  CARD_NAME as ProjectListCardName,
  MasterOptions as ProjectListMasterOptions,
  OnEvent,
  PickedState,
  RunSequence as ProjectListRunSequence,
  SetProp as ProjectListSetProp,
  State as ProjectListState,
} from "./sequence/ProjectList";

import {
  CARD_NAME as IssueListCardName,
  MasterOptions as IssueListMasterOptions,
  RunSequence as IssueListRunSequence,
  SetProp as IssueListSetProp,
  State as IssueListState,
} from "./sequence/IssueList";

// Type
type WorkPlanProps = {
  onEvent: OnEvent;
  setProp: ProjectListSetProp & IssueListSetProp;
  runSequence: ProjectListRunSequence & IssueListRunSequence;
  ProjectListSequence: ProjectListState["ProjectListSequence"];
  IssueListSequence: IssueListState["IssueListSequence"];
  masterOptions: ProjectListMasterOptions & IssueListMasterOptions;
  languageUX: "en" | "th";
} & Pick<
  PickedState,
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "adminMenuDivisions" | "selectedDivision"
>;

type TabMenu = "ProjectList" | "IssueList";

const TAB_MENU_MAPPING = {
  // Project list
  [ADMIN_TAB_LIST["Project list"]]: "ProjectList",
  // Issue list
  [ADMIN_TAB_LIST["Issue list"]]: "IssueList",
} as const;

const CardWorkPlan = (props: WorkPlanProps) => {
  const [activeTab, setActiveTab] = useState<"" | TabMenu>("ProjectList");

  const filteredTabMenu = useMemo(() => {
    const tabs = props.adminMenuDivisions?.all["Work & Plan System"]?.tabs ?? [];

    return Object.entries(TAB_MENU_MAPPING)
      .filter(([key]) => tabs.includes(key))
      .map((menu) => menu[1]);
  }, [props.adminMenuDivisions]);

  useEffect(() => {
    setActiveTab(filteredTabMenu[0] || "");
  }, [filteredTabMenu]);

  const handleChangeTab = (tab: TabMenu) => {
    if (tab === activeTab) return;

    setActiveTab(tab);
  };

  const currentCardName = useMemo(() => {
    switch (activeTab) {
      case "ProjectList":
        return ProjectListCardName;
      case "IssueList":
        return IssueListCardName;
      default:
        return "";
    }
  }, [activeTab]);

  const displayTab = useMemo(() => {
    switch (activeTab) {
      case "ProjectList":
        return (
          <CardProjectList
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            ProjectListSequence={props.ProjectListSequence}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            successMessage={props.successMessage}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      case "IssueList":
        return (
          <CardIssueList
            onEvent={props.onEvent}
            setProp={props.setProp}
            runSequence={props.runSequence}
            IssueListSequence={props.IssueListSequence}
            buttonLoadCheck={props.buttonLoadCheck}
            errorMessage={props.errorMessage}
            successMessage={props.successMessage}
            masterOptions={props.masterOptions}
            languageUX={props.languageUX}
          />
        );
      default:
        return null;
    }
  }, [
    activeTab,
    props.buttonLoadCheck,
    props.errorMessage,
    props.successMessage,
    props.masterOptions,
    props.ProjectListSequence,
    props.IssueListSequence,
  ]);

  return (
    <>
      <SnackMessage
        // func
        onEvent={props.onEvent}
        onClose={() => {
          props.setProp(`errorMessage.${currentCardName}`, null);
          props.setProp(`successMessage.${currentCardName}`, null);
        }}
        error={props.errorMessage?.[currentCardName]}
        success={props.successMessage?.[currentCardName]}
        languageUX={props.languageUX}
      />

      <CardWorkPlanUX
        activeTab={activeTab}
        displayTab={displayTab}
        filteredTabMenu={filteredTabMenu}
        onHandleChangeTab={handleChangeTab}
      />
    </>
  );
};

export default CardWorkPlan;
